import supabase from '../supabase/supabase';

const BANK_CONNECTIONS_TABLE = 'bank_accounts';

const PLAID_SYNC_FUNCTION = 'plaid-sync-accounts';
const PLAID_TRANSACTION_SYNC_FUNCTION = 'plaid-sync-transactions';

const BANK_MAPPING_RPC = 'set_bank_account_mapping';
const BANK_DISCONNECT_RPC = 'disconnect_bank_account';

class BankService {
  /**
   * Verify that the current user belongs to the organization.
   */
  async verifyOrganizationAccess(organizationId) {
    if (!organizationId) {
      throw new Error(
        'No active organization was selected. Please select an organization first.'
      );
    }

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError) {
      throw userError;
    }

    if (!user) {
      throw new Error(
        'Your session has expired. Please sign in again.'
      );
    }

    const { data, error } = await supabase
      .schema('accounting')
      .from('organization_users')
      .select('id, organization_id, role')
      .eq('organization_id', organizationId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error(
        'Failed to verify organization access:',
        error
      );
      throw error;
    }

    if (!data) {
      throw new Error(
        'You do not have access to the selected organization.'
      );
    }

    return data;
  }

  /**
   * Verify the user's permission to manage banking.
   *
   * Current database roles:
   * - owner
   * - client
   *
   * Only owners can modify mappings or disconnect banks.
   *
   * The RPC functions must independently enforce
   * the same authorization rule.
   */
  async verifyBankManagementAccess(organizationId) {
    const membership = await this.verifyOrganizationAccess(
      organizationId
    );

    if (membership.role !== 'owner') {
      throw new Error(
        'Only an organization owner can manage bank account mappings or disconnect bank accounts.'
      );
    }

    return membership;
  }

  /**
   * Normalize bank account data before returning it to the UI.
   */
  normalizeBankConnection(connection) {
    if (!connection) {
      return null;
    }

    return {
      ...connection,
      last_sync_at:
        connection.last_sync_at ||
        connection.updated_at ||
        connection.created_at,
    };
  }

  /**
   * Get all active bank accounts for the selected organization.
   */
  async getConnectedBanks(organizationId) {
    await this.verifyOrganizationAccess(organizationId);

    const { data, error } = await supabase
      .schema('accounting')
      .from(BANK_CONNECTIONS_TABLE)
      .select(`
        id,
        organization_id,
        account_id,
        institution_name,
        account_name,
        account_type,
        mask,
        balance,
        plaid_item_id,
        plaid_account_id,
        is_active,
        disconnected_at,
        last_sync_at,
        created_at,
        updated_at
      `)
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .order('created_at', {
        ascending: false,
      });

    if (error) {
      console.error(
        'Failed to load connected bank accounts:',
        error
      );
      throw error;
    }

    return (data || []).map((connection) =>
      this.normalizeBankConnection(connection)
    );
  }

  /**
   * Bank connections must be created through Plaid.
   */
  async connectBank() {
    throw new Error(
      'Bank connections must be created through the Plaid connection flow.'
    );
  }

  /**
   * Securely soft-disconnect a bank account.
   *
   * Uses the accounting.disconnect_bank_account RPC.
   *
   * Preserves the existing method signature:
   * removeBank(id, organizationId)
   *
   * Preserves the existing return value: true.
   */
  async removeBank(id, organizationId) {
    if (!id) {
      throw new Error('Bank account ID is required.');
    }

    if (!organizationId) {
      throw new Error('Organization ID is required.');
    }

    await this.verifyBankManagementAccess(organizationId);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc(BANK_DISCONNECT_RPC, {
        p_bank_account_id: id,
        p_organization_id: organizationId,
      });

    if (error) {
      console.error(
        'Failed to disconnect bank account:',
        error
      );

      throw new Error(
        `Unable to disconnect bank account: ${error.message}`
      );
    }

    if (!data?.id || data.is_active !== false) {
      throw new Error(
        'Bank account disconnection could not be confirmed.'
      );
    }

    return true;
  }

  /**
   * Synchronize all bank accounts belonging to a Plaid Item.
   *
   * Transaction synchronization is handled separately.
   */
  async syncPlaidItem(itemId, organizationId) {
    if (!itemId) {
      throw new Error('Plaid Item ID is required.');
    }

    if (!organizationId) {
      throw new Error(
        'Please select an organization before synchronizing the bank connection.'
      );
    }

    await this.verifyOrganizationAccess(organizationId);

    const { data, error } = await supabase.functions.invoke(
      PLAID_SYNC_FUNCTION,
      {
        body: {
          organizationId,
          itemId,
        },
      }
    );

    if (error) {
      console.error(
        'Failed to synchronize Plaid bank accounts:',
        error
      );
      throw error;
    }

    if (!data?.success) {
      throw new Error(
        data?.error ||
          'Unable to synchronize the connected bank accounts.'
      );
    }

    return data;
  }

  /**
   * Synchronize transactions for one selected bank account.
   *
   * Existing Edge Function:
   * plaid-sync-transactions
   *
   * The Edge Function uses Plaid /transactions/get
   * with options.account_ids.
   *
   * No Plaid cursor is managed by the frontend.
   */
  async syncPlaidTransactions(
    itemId,
    organizationId,
    bankAccountId = null
  ) {
    if (!itemId) {
      throw new Error('Plaid Item ID is required.');
    }

    if (!organizationId) {
      throw new Error(
        'Please select an organization before synchronizing transactions.'
      );
    }

    if (!bankAccountId) {
      throw new Error(
        'Bank account ID is required to synchronize transactions.'
      );
    }

    await this.verifyOrganizationAccess(organizationId);

    const requestBody = {
      organizationId,
      itemId,
      bankAccountId,
    };

    console.log('Syncing Plaid transactions:', requestBody);

    const { data, error } = await supabase.functions.invoke(
      PLAID_TRANSACTION_SYNC_FUNCTION,
      {
        body: requestBody,
      }
    );

    if (error) {
      console.error(
        'Failed to synchronize Plaid transactions:',
        error
      );

      let responseData = null;

      try {
        if (
          error?.context &&
          typeof error.context.json === 'function'
        ) {
          responseData = await error.context.json();
        }
      } catch (parseError) {
        console.warn(
          'Unable to parse Plaid transaction sync error response:',
          parseError
        );
      }

      const syncError = new Error(
        responseData?.error ||
          error?.message ||
          'Unable to synchronize Plaid transactions.'
      );

      syncError.code = responseData?.code || null;
      syncError.accounts =
        responseData?.unmappedAccounts || [];
      syncError.unmappedAccounts =
        responseData?.unmappedAccounts || [];
      syncError.responseData = responseData;

      throw syncError;
    }

    if (!data?.success) {
      const syncError = new Error(
        data?.error ||
          'Unable to synchronize Plaid transactions.'
      );

      syncError.code = data?.code || null;
      syncError.accounts = data?.unmappedAccounts || [];
      syncError.unmappedAccounts =
        data?.unmappedAccounts || [];
      syncError.responseData = data;

      throw syncError;
    }

    /**
     * Normalize the existing Edge Function response.
     */
    const importBatchId =
      data?.importBatchId ||
      data?.transactionSync?.importBatchId ||
      null;

    const stagedCount = Number(
      data?.staged ??
        data?.stagedCount ??
        data?.transactionSync?.staged ??
        data?.transactionSync?.stagedCount ??
        0
    );

    const selectedStagedCount = Number(
      data?.selectedStaged ??
        data?.selectedAccountSync?.staged ??
        data?.transactionSync?.selectedStaged ??
        data?.transactionSync?.selectedAccountSync?.staged ??
        data?.selectedAccountSync?.added ??
        data?.transactionSync?.selectedAccountSync?.added ??
        0
    );

    const selectedModifiedCount = Number(
      data?.selectedAccountSync?.modified ?? 0
    );

    const selectedRemovedCount = Number(
      data?.selectedAccountSync?.removed ?? 0
    );

    const itemAddedCount = Number(
      data?.itemSync?.added ?? 0
    );

    const itemModifiedCount = Number(
      data?.itemSync?.modified ?? 0
    );

    const itemRemovedCount = Number(
      data?.itemSync?.removed ?? 0
    );

    const duplicateCount = Number(
      data?.duplicates ??
        data?.duplicateCount ??
        data?.transactionSync?.duplicates ??
        0
    );

    const selectedDuplicateCount = Number(
      data?.selectedDuplicates ??
        data?.selectedAccountSync?.duplicates ??
        0
    );

    const modifiedUpdated = Number(
      data?.modifiedUpdated ?? 0
    );

    const removedRejected = Number(
      data?.removedRejected ?? 0
    );

    if (stagedCount > 0 && !importBatchId) {
      throw new Error(
        'Plaid transactions were staged, but the synchronization response did not include the importBatchId.'
      );
    }

    return {
      ...data,

      bankAccountId:
        data?.selectedBankAccount?.id ||
        data?.bankAccountId ||
        data?.transactionSync?.bankAccountId ||
        bankAccountId,

      plaidAccountId:
        data?.selectedBankAccount?.plaidAccountId ||
        data?.plaidAccountId ||
        data?.transactionSync?.plaidAccountId ||
        null,

      selectedBankAccount:
        data?.selectedBankAccount || null,

      importBatchId,

      staged: stagedCount,
      selectedStaged: selectedStagedCount,

      selectedModified: selectedModifiedCount,
      selectedRemoved: selectedRemovedCount,

      itemAdded: itemAddedCount,
      itemModified: itemModifiedCount,
      itemRemoved: itemRemovedCount,

      duplicates: duplicateCount,
      selectedDuplicates: selectedDuplicateCount,

      modifiedUpdated,
      removedRejected,

      // Preserve the backend's actual synchronization scope.
      syncScope: data?.syncScope || 'plaid_account',

      selectedAccountOnlyForUi:
        data?.selectedAccountOnlyForUi ?? true,

      cursorSaved: data?.cursorSaved ?? false,

      pending:
        data?.pending ??
        data?.pendingCount ??
        0,

      skipped:
        data?.skipped ??
        data?.skippedCount ??
        0,

      unmappedAccounts:
        data?.unmappedAccounts ||
        data?.transactionSync?.unmappedAccounts ||
        [],
    };
  }

  /**
   * Synchronize one selected connected bank account.
   *
   * Preserves the existing UI response contract.
   */
  async syncBank(accountId, organizationId) {
    try {
      if (!accountId) {
        throw new Error(
          'Bank account ID is required.'
        );
      }

      if (!organizationId) {
        throw new Error(
          'Organization ID is required.'
        );
      }

      await this.verifyOrganizationAccess(organizationId);

      const { data: account, error: accountError } =
        await supabase
          .schema('accounting')
          .from(BANK_CONNECTIONS_TABLE)
          .select(`
            id,
            organization_id,
            account_id,
            institution_name,
            account_name,
            account_type,
            mask,
            plaid_item_id,
            plaid_account_id,
            is_active,
            disconnected_at,
            last_sync_at
          `)
          .eq('id', accountId)
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .maybeSingle();

      if (accountError) {
        throw accountError;
      }

      if (!account) {
        throw new Error(
          'Bank account not found or it is inactive.'
        );
      }

      if (!account.account_id) {
        throw new Error(
          'Map this bank account to a ProfitYO account before synchronizing transactions.'
        );
      }

      if (!account.plaid_item_id) {
        throw new Error(
          'This bank account is not linked to a Plaid Item.'
        );
      }

      if (!account.plaid_account_id) {
        throw new Error(
          'This bank account does not have a Plaid Account ID.'
        );
      }

      console.log('Bank synchronization identifiers:', {
        organizationId,
        bankAccountId: account.id,
        sourceAccountId: account.account_id,
        plaidItemId: account.plaid_item_id,
        plaidAccountId: account.plaid_account_id,
      });

      const transactionSync =
        await this.syncPlaidTransactions(
          account.plaid_item_id,
          organizationId,
          account.id
        );

      return {
        account,
        transactionSync,

        selectedStaged:
          transactionSync?.selectedStaged ?? 0,

        selectedModified:
          transactionSync?.selectedModified ?? 0,

        selectedRemoved:
          transactionSync?.selectedRemoved ?? 0,

        selectedDuplicates:
          transactionSync?.selectedDuplicates ?? 0,

        importBatchId:
          transactionSync?.importBatchId ?? null,
      };
    } catch (error) {
      console.error('Bank sync failed:', error);
      throw error;
    }
  }

  /**
   * Get all active Chart of Accounts for an organization.
   */
  async getActiveAccounts(organizationId) {
    await this.verifyOrganizationAccess(organizationId);

    const { data, error } = await supabase
      .schema('accounting')
      .from('accounts')
      .select(`
        id,
        organization_id,
        code,
        name,
        type,
        sub_type,
        currency,
        is_active
      `)
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .order('code', {
        ascending: true,
        nullsFirst: false,
      })
      .order('name', {
        ascending: true,
      });

    if (error) {
      console.error(
        'Failed to load accounting accounts:',
        error
      );
      throw error;
    }

    return data || [];
  }

  /**
   * Securely map a bank account to the Chart of Accounts.
   *
   * Uses accounting.set_bank_account_mapping RPC.
   *
   * Preserves the existing method signature and return type.
   */
  async updateBankAccountMapping(
    bankAccountId,
    organizationId,
    accountId
  ) {
    if (!bankAccountId) {
      throw new Error(
        'Bank account ID is required.'
      );
    }

    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    if (!accountId) {
      throw new Error(
        'ProfitYO account is required.'
      );
    }

    await this.verifyBankManagementAccess(organizationId);

    const { data, error } = await supabase
      .schema('accounting')
      .rpc(BANK_MAPPING_RPC, {
        p_bank_account_id: bankAccountId,
        p_organization_id: organizationId,
        p_account_id: accountId,
      });

    if (error) {
      console.error(
        'Failed to update bank account mapping:',
        error
      );

      throw new Error(
        `Unable to update bank account mapping: ${error.message}`
      );
    }

    if (!data?.id) {
      throw new Error(
        'Bank mapping update did not return a valid account.'
      );
    }

    return this.normalizeBankConnection(data);
  }
}

export const bankService = new BankService();